"use client"

import * as React from "react"
import { type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { textAreaLabelVariants, textAreaVariants } from "./text-area.variants"

export interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textAreaVariants> {
  label?: string
  exceededCharacters?: number
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      size,
      invalid,
      border,
      label,
      className,
      id,
      value,
      defaultValue,
      placeholder,
      onChange,
      exceededCharacters,
      ...props
    },
    ref
  ) => {
    const [hasValue, setHasValue] = React.useState(
      !!value || !!defaultValue || !!placeholder
    )

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHasValue(!!e.target.value || !!placeholder)
      onChange?.(e)
    }

    React.useEffect(() => {
      setHasValue(!!value || !!defaultValue || !!placeholder)
    }, [value, defaultValue, placeholder])

    return (
      <div className="relative">
        <textarea
          className={cn(
            textAreaVariants({ size, invalid, border }),
            "peer",
            className
          )}
          id={id}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          onChange={handleChange}
          {...props}
        />

        {label && (
          <label
            htmlFor={id}
            className={cn(
              textAreaLabelVariants({ size, invalid, border }),
              hasValue && "-top-2 px-1"
            )}
          >
            {label}
          </label>
        )}

        {invalid && exceededCharacters ? (
          <span className="right-6 bottom-3 relative text-red-500 text-sm">
            -{Math.abs(exceededCharacters)}
          </span>
        ) : null}
      </div>
    )
  }
)
TextArea.displayName = "TextArea"

export { TextArea }
